/**
 * @file components/RoleGuard.jsx
 * @description Client-side role guard that hides pages from users without
 * the required application role.
 *
 * The role is fetched from /api/users/me on first render and cached in
 * React Query. This is a UI-only guard — the backend enforces roles on every
 * request independently.
 *
 * @param {Object}          props
 * @param {string[]}        props.allowedRoles - Array of roles that may view this route.
 * @param {React.ReactNode} props.children     - Page component to render if allowed.
 */

import React from 'react';
import { useQuery }       from '@tanstack/react-query';
import apiClient          from '../api/client';

/**
 * Fetches the current user's local record (including their application role).
 * The endpoint is authenticated via the Entra ID token injected by the Axios interceptor.
 *
 * @returns {Promise<{ id: number, role: string, name: string }>}
 */
const fetchCurrentUser = async () => {
  const res = await apiClient.get('/api/users/me');
  return res.data;
};

/**
 * Renders children when the current user's role is in allowedRoles.
 * Shows a 403-style message otherwise.
 */
export default function RoleGuard({ allowedRoles, children }) {
  const { data: user, isLoading, isError } = useQuery({
    queryKey: ['currentUser'],
    queryFn:  fetchCurrentUser,
    // Cache for 10 minutes — roles rarely change mid-session
    staleTime: 10 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div
          className="spinner-border"
          style={{ color: 'var(--color-primary)' }}
          role="status"
        >
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  // If we couldn't determine the role, deny access silently
  if (isError || !user) return null;

  if (!allowedRoles.includes(user.role)) {
    return (
      <div className="page-content">
        <div className="alert alert-warning d-flex align-items-center gap-2" role="alert">
          <i className="bi bi-shield-exclamation fs-4" aria-hidden="true" />
          <div>
            <strong>Access denied.</strong> You need one of the following roles to view this page:{' '}
            <strong>{allowedRoles.join(', ')}</strong>.
          </div>
        </div>
      </div>
    );
  }

  return children;
}
